import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ToUppercaseDirective } from '@app/shared/pipes/to-uppercase.directive';
import { Residency, ResidencyFormGroup } from '../residency.model';

@Component({
  selector: 'app-residency-address-form',
  imports: [
    ReactiveFormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ToUppercaseDirective,
  ],
  templateUrl: './residency-address-form.html',
  styleUrls: ['./residency-address-form.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResidencyAddressForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ResidencyAddressForm),
      multi: true,
    },
  ],
})
export class ResidencyAddressForm
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator
{
  @Input({ required: true }) formGroup!: ResidencyFormGroup;
  @Input() required: boolean | string | null | undefined;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<Residency>();
  public ngControl?: NgControl;
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnInit(): void {
    // Intentionally left empty
  }

  ngOnChanges(): void {
    this.applyDisabledState();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnDestroy(): void {
    // Intentionally left empty
  }

  get isValid(): boolean {
    return this.formGroup.valid;
  }

  onChange: (value: Residency) => void = () => {
    // Intentionally left empty
  };
  onTouched: () => void = () => {
    // Intentionally left empty
  };

  writeValue(value: Residency): void {
    if (value) {
      this.formGroup.patchValue(value);
    } else {
      this.formGroup.reset();
    }
  }

  registerOnChange(fn: (value: Residency) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.errors;
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null && this.required !== '') {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }
    return this.control?.hasValidator(Validators.required) ?? false;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.applyDisabledState();
  }

  private applyDisabledState(): void {
    if (!this.formGroup) {
      return;
    }

    if (this.disabled) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
    }
  }
}
